/**
 * WeaveIn.ai Platform - Caregivers Tab Component
 * Manages caregiver authorization and access for a member
 */

import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/services/apiClient';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import PhoneInput from '@/components/ui/PhoneInput';

// ============================================================================
// Types
// ============================================================================

interface CaregiverPermissions {
  view_profile: boolean;
  view_health_records: boolean;
  schedule_appointments: boolean;
  manage_medications: boolean;
  access_financial: boolean;
  communicate_providers: boolean;
  make_decisions: boolean;
  emergency_contact: boolean;
}

interface Caregiver {
  id: number;
  member_id: number;
  caregiver_type: string;
  family_member_id?: number;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  relationship?: string;
  organization_name?: string;
  license_number?: string;
  license_state?: string;
  authorization_level: string;
  permissions: CaregiverPermissions;
  authorization_start_date: string;
  authorization_end_date?: string;
  status: string;
  approved_by?: number;
  approved_at?: string;
  notes?: string;
  created_at: string;
  updated_at?: string;
}

interface CaregiverAccessLog {
  id: number;
  caregiver_id: number;
  access_type: string;
  resource_type: string;
  resource_id?: number;
  access_timestamp: string;
  ip_address?: string;
  user_agent?: string;
  notes?: string;
}

interface CaregiverCreate {
  caregiver_type: string;
  family_member_id?: number;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  relationship?: string;
  organization_name?: string;
  license_number?: string;
  license_state?: string;
  authorization_level?: string;
  permissions?: Partial<CaregiverPermissions>;
  authorization_start_date: string;
  authorization_end_date?: string;
  notes?: string;
}

interface CaregiversTabProps {
  memberId: number;
  isReadOnly?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const CAREGIVER_TYPES = [
  { value: 'FAMILY', label: 'Family Member' },
  { value: 'PROFESSIONAL', label: 'Professional Caregiver' },
  { value: 'LEGAL_GUARDIAN', label: 'Legal Guardian' },
  { value: 'HEALTHCARE_PROXY', label: 'Healthcare Proxy' },
  { value: 'POWER_OF_ATTORNEY', label: 'Power of Attorney' },
  { value: 'CASE_MANAGER', label: 'Case Manager' },
  { value: 'SOCIAL_WORKER', label: 'Social Worker' },
  { value: 'OTHER', label: 'Other' },
];

const AUTHORIZATION_LEVELS = [
  { value: 'VIEW_ONLY', label: 'View Only', description: 'Can only view basic profile information' },
  { value: 'LIMITED', label: 'Limited', description: 'Can view and schedule appointments' },
  { value: 'FULL', label: 'Full Access', description: 'Full access with decision-making authority' },
];

const RELATIONSHIP_OPTIONS = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'child', label: 'Child' },
  { value: 'parent', label: 'Parent' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'friend', label: 'Friend' },
  { value: 'neighbor', label: 'Neighbor' },
  { value: 'professional', label: 'Professional' },
  { value: 'other', label: 'Other' },
];

const DEFAULT_PERMISSIONS: CaregiverPermissions = {
  view_profile: true,
  view_health_records: false,
  schedule_appointments: false,
  manage_medications: false,
  access_financial: false,
  communicate_providers: false,
  make_decisions: false,
  emergency_contact: false,
};

// ============================================================================
// Component
// ============================================================================

export default function CaregiversTab({ memberId, isReadOnly = false }: CaregiversTabProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [caregivers, setCaregivers] = useState<Caregiver[]>([]);
  const [accessLogs, setAccessLogs] = useState<CaregiverAccessLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAccessLogs, setShowAccessLogs] = useState(false);
  const [selectedCaregiver, setSelectedCaregiver] = useState<Caregiver | null>(null);
  const [editingCaregiver, setEditingCaregiver] = useState<Caregiver | null>(null);
  const [formData, setFormData] = useState<CaregiverCreate>({
    caregiver_type: 'FAMILY',
    first_name: '',
    last_name: '',
    authorization_level: 'VIEW_ONLY',
    authorization_start_date: new Date().toISOString().split('T')[0],
    permissions: { ...DEFAULT_PERMISSIONS },
  });
  const [saving, setSaving] = useState(false);

  // Load caregivers
  const loadCaregivers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/api/members/${memberId}/caregivers`);
      setCaregivers(response.data);
    } catch (err: any) {
      console.error('Failed to load caregivers:', err);
      setError(err.response?.data?.detail || 'Failed to load caregivers');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  // Load access logs for a caregiver
  const loadAccessLogs = async (caregiverId: number) => {
    try {
      const response = await apiClient.get(`/api/members/${memberId}/caregivers/${caregiverId}/access-log`);
      setAccessLogs(response.data);
    } catch (err: any) {
      console.error('Failed to load access logs:', err);
    }
  };

  useEffect(() => {
    loadCaregivers();
  }, [loadCaregivers]);

  // Reset form
  const resetForm = () => {
    setFormData({
      caregiver_type: 'FAMILY',
      first_name: '',
      last_name: '',
      authorization_level: 'VIEW_ONLY',
      authorization_start_date: new Date().toISOString().split('T')[0],
      permissions: { ...DEFAULT_PERMISSIONS },
    });
    setEditingCaregiver(null);
    setShowAddForm(false);
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingCaregiver) {
        await apiClient.put(`/api/members/${memberId}/caregivers/${editingCaregiver.id}`, formData);
      } else {
        await apiClient.post(`/api/members/${memberId}/caregivers`, formData);
      }
      resetForm();
      loadCaregivers();
    } catch (err: any) {
      console.error('Failed to save caregiver:', err);
      setError(err.response?.data?.detail || 'Failed to save caregiver');
    } finally {
      setSaving(false);
    }
  };

  // Handle approve
  const handleApprove = async (caregiverId: number) => {
    try {
      await apiClient.post(`/api/members/${memberId}/caregivers/${caregiverId}/approve`);
      loadCaregivers();
    } catch (err: any) {
      console.error('Failed to approve caregiver:', err);
      setError(err.response?.data?.detail || 'Failed to approve caregiver');
    }
  };

  // Handle revoke
  const handleRevoke = async (caregiverId: number) => {
    if (!window.confirm('Are you sure you want to revoke this caregiver\'s access?')) {
      return;
    }

    try {
      await apiClient.post(`/api/members/${memberId}/caregivers/${caregiverId}/revoke`);
      loadCaregivers();
    } catch (err: any) {
      console.error('Failed to revoke caregiver:', err);
      setError(err.response?.data?.detail || 'Failed to revoke caregiver access');
    }
  };

  // Handle delete
  const handleDelete = async (caregiverId: number) => {
    if (!window.confirm('Are you sure you want to remove this caregiver?')) {
      return;
    }

    try {
      await apiClient.delete(`/api/members/${memberId}/caregivers/${caregiverId}`);
      loadCaregivers();
    } catch (err: any) {
      console.error('Failed to delete caregiver:', err);
      setError(err.response?.data?.detail || 'Failed to delete caregiver');
    }
  };

  // Handle edit
  const handleEdit = (caregiver: Caregiver) => {
    setEditingCaregiver(caregiver);
    setFormData({
      caregiver_type: caregiver.caregiver_type,
      first_name: caregiver.first_name,
      last_name: caregiver.last_name,
      email: caregiver.email,
      phone: caregiver.phone,
      relationship: caregiver.relationship,
      organization_name: caregiver.organization_name,
      license_number: caregiver.license_number,
      license_state: caregiver.license_state,
      authorization_level: caregiver.authorization_level,
      permissions: caregiver.permissions,
      authorization_start_date: caregiver.authorization_start_date,
      authorization_end_date: caregiver.authorization_end_date,
      notes: caregiver.notes,
    });
    setShowAddForm(true);
  };

  // Handle view access logs
  const handleViewAccessLogs = (caregiver: Caregiver) => {
    setSelectedCaregiver(caregiver);
    loadAccessLogs(caregiver.id);
    setShowAccessLogs(true);
  };

  // Update permissions based on authorization level
  const updatePermissionsForLevel = (level: string) => {
    let newPermissions: CaregiverPermissions;
    switch (level) {
      case 'VIEW_ONLY':
        newPermissions = {
          view_profile: true,
          view_health_records: false,
          schedule_appointments: false,
          manage_medications: false,
          access_financial: false,
          communicate_providers: false,
          make_decisions: false,
          emergency_contact: false,
        };
        break;
      case 'LIMITED':
        newPermissions = {
          view_profile: true,
          view_health_records: true,
          schedule_appointments: true,
          manage_medications: false,
          access_financial: false,
          communicate_providers: true,
          make_decisions: false,
          emergency_contact: true,
        };
        break;
      case 'FULL':
        newPermissions = {
          view_profile: true,
          view_health_records: true,
          schedule_appointments: true,
          manage_medications: true,
          access_financial: true,
          communicate_providers: true,
          make_decisions: true,
          emergency_contact: true,
        };
        break;
      default:
        newPermissions = { ...DEFAULT_PERMISSIONS };
    }
    setFormData({ ...formData, authorization_level: level, permissions: newPermissions });
  };

  // Get caregiver type label
  const getCaregiverTypeLabel = (value: string) => {
    return CAREGIVER_TYPES.find(ct => ct.value === value)?.label || value;
  };

  // Get authorization level label
  const getAuthLevelLabel = (value: string) => {
    return AUTHORIZATION_LEVELS.find(l => l.value === value)?.label || value;
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'REVOKED':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'EXPIRED':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  // Get authorization level color
  const getAuthLevelColor = (level: string) => {
    switch (level) {
      case 'VIEW_ONLY':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'LIMITED':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'FULL':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('members.caregivers', 'Caregivers')}
          <span className="ml-2 text-sm font-normal text-gray-500">
            ({caregivers.length} {caregivers.length === 1 ? 'caregiver' : 'caregivers'})
          </span>
        </h3>
        {!isReadOnly && !showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-v2accent text-white rounded-lg hover:bg-v2accent/90 transition-colors"
          >
            + Add Caregiver
          </button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="mb-6 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <h4 className="text-md font-semibold mb-4 text-gray-900 dark:text-white">
            {editingCaregiver ? 'Edit Caregiver' : 'Add Caregiver'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Row 1: Caregiver Type, First Name, Last Name */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Caregiver Type *
                </label>
                <select
                  value={formData.caregiver_type}
                  onChange={(e) => setFormData({ ...formData, caregiver_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  {CAREGIVER_TYPES.map(ct => (
                    <option key={ct.value} value={ct.value}>{ct.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
            </div>

            {/* Row 2: Email, Phone, Relationship */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone
                </label>
                <PhoneInput
                  value={formData.phone || ''}
                  onChange={(v) => setFormData({ ...formData, phone: v })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Relationship
                </label>
                <select
                  value={formData.relationship || ''}
                  onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select...</option>
                  {RELATIONSHIP_OPTIONS.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 3: Professional fields (conditional) */}
            {(formData.caregiver_type === 'PROFESSIONAL' || formData.caregiver_type === 'CASE_MANAGER' || formData.caregiver_type === 'SOCIAL_WORKER') && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Organization
                  </label>
                  <input
                    type="text"
                    value={formData.organization_name || ''}
                    onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    License Number
                  </label>
                  <input
                    type="text"
                    value={formData.license_number || ''}
                    onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    License State
                  </label>
                  <input
                    type="text"
                    value={formData.license_state || ''}
                    onChange={(e) => setFormData({ ...formData, license_state: e.target.value.toUpperCase().slice(0, 2) })}
                    placeholder="CA"
                    maxLength={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            )}

            {/* Row 4: Authorization Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Authorization Level *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {AUTHORIZATION_LEVELS.map(level => (
                  <div
                    key={level.value}
                    onClick={() => updatePermissionsForLevel(level.value)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      formData.authorization_level === level.value
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                        : 'border-gray-200 dark:border-gray-600 hover:border-blue-300'
                    }`}
                  >
                    <div className="font-medium text-gray-900 dark:text-white">{level.label}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{level.description}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Row 5: Granular Permissions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Permissions
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(formData.permissions || {}).map(([key, value]) => (
                  <div key={key} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`perm-${key}`}
                      checked={value}
                      onChange={(e) => setFormData({
                        ...formData,
                        permissions: { ...formData.permissions, [key]: e.target.checked } as CaregiverPermissions,
                      })}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <label htmlFor={`perm-${key}`} className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize">
                      {key.replace(/_/g, ' ')}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Row 6: Authorization Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Authorization Start Date *
                </label>
                <input
                  type="date"
                  value={formData.authorization_start_date}
                  onChange={(e) => setFormData({ ...formData, authorization_start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Authorization End Date
                </label>
                <input
                  type="date"
                  value={formData.authorization_end_date || ''}
                  onChange={(e) => setFormData({ ...formData, authorization_end_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {/* Row 7: Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Form buttons */}
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-v2accent text-white rounded-lg hover:bg-v2accent/90 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : editingCaregiver ? 'Update' : 'Add Caregiver'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Access Logs Modal */}
      {showAccessLogs && selectedCaregiver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                Access Log: {selectedCaregiver.first_name} {selectedCaregiver.last_name}
              </h4>
              <button
                onClick={() => setShowAccessLogs(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {accessLogs.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">No access logs found</p>
              ) : (
                <div className="space-y-3">
                  {accessLogs.map(log => (
                    <div key={log.id} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="font-medium text-gray-900 dark:text-white">{log.access_type}</span>
                          <span className="text-gray-500 dark:text-gray-400"> - {log.resource_type}</span>
                        </div>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {fmt.dateTime(log.access_timestamp)}
                        </span>
                      </div>
                      {log.notes && (
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{log.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Caregivers List */}
      {caregivers.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
          <p className="mt-2">No caregivers assigned yet</p>
          {!isReadOnly && (
            <button
              onClick={() => setShowAddForm(true)}
              className="mt-3 text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              Add the first caregiver
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {caregivers.map((caregiver) => (
            <div
              key={caregiver.id}
              className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                      {caregiver.first_name} {caregiver.last_name}
                    </h4>
                    <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      {getCaregiverTypeLabel(caregiver.caregiver_type)}
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${getAuthLevelColor(caregiver.authorization_level)}`}>
                      {getAuthLevelLabel(caregiver.authorization_level)}
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(caregiver.status)}`}>
                      {caregiver.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {caregiver.phone && (
                      <div>
                        <span className="font-medium">Phone:</span> {caregiver.phone}
                      </div>
                    )}
                    {caregiver.email && (
                      <div>
                        <span className="font-medium">Email:</span> {caregiver.email}
                      </div>
                    )}
                    {caregiver.relationship && (
                      <div>
                        <span className="font-medium">Relationship:</span> {caregiver.relationship}
                      </div>
                    )}
                    <div>
                      <span className="font-medium">Since:</span> {fmt.short(caregiver.authorization_start_date)}
                    </div>
                  </div>

                  {/* Permissions badges */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {Object.entries(caregiver.permissions || {}).filter(([, v]) => v).map(([key]) => (
                      <span
                        key={key}
                        className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 capitalize"
                      >
                        {key.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>

                {!isReadOnly && (
                  <div className="flex gap-2 ml-4">
                    {caregiver.status === 'PENDING' && (
                      <button
                        onClick={() => handleApprove(caregiver.id)}
                        className="p-2 text-green-600 hover:text-green-700 dark:text-green-400"
                        title="Approve"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                    )}
                    {caregiver.status === 'ACTIVE' && (
                      <button
                        onClick={() => handleRevoke(caregiver.id)}
                        className="p-2 text-orange-600 hover:text-orange-700 dark:text-orange-400"
                        title="Revoke Access"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => handleViewAccessLogs(caregiver)}
                      className="p-2 text-gray-600 hover:text-gray-700 dark:text-gray-400"
                      title="View Access Log"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleEdit(caregiver)}
                      className="p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400"
                      title="Edit"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(caregiver.id)}
                      className="p-2 text-red-600 hover:text-red-700 dark:text-red-400"
                      title="Delete"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
